import { computed, ref, watch } from 'vue';
import { getHostRouter } from '@Src/mfe.config';
import { startParticipantInitiatedPaymentApi } from '@Api/dashboard.api';
import { useDashboardStore } from '@Stores/dashboard.store';
import { isPaymentInitiatorRole } from '@Src/shared/utils/participantRole';
import { withCompanySlug } from '@Src/shared/utils/withCompanySlug';
import { ParticipantPaymentTracking } from '@Analytics';

/**
 * Presentation for each purpose the BFF can offer, taken verbatim from the
 * certifid-100-vision prototype's en.json (portal.buyerInitiated.sheet.*).
 *
 * Icons are substitutions, not matches. The prototype uses lucide HandCoinsIcon
 * for earnest money and KeyRoundIcon for cash to close; uilib has neither, so
 * this maps to the nearest available semantics: a protected-money mark for the
 * good-faith deposit, and a home mark for the funds that close on the house.
 * Worth a design confirmation.
 *
 * A purpose the BFF sends that is absent here is dropped rather than shown under
 * a fabricated label, matching how payments-mfe treats unknown server purposes.
 */
const PURPOSE_PRESENTATION: Record<string, { label: string; helper: string; icon: string }> = {
    EMD: {
        label: 'Earnest money deposit',
        helper: 'The good-faith deposit to secure the property.',
        icon: 'cd-ic-money-shield',
    },
    CashToClose: {
        label: 'Cash to close',
        helper: 'The remaining funds due to close on your home.',
        icon: 'cd-ic-home',
    },
};

/**
 * Closing-fund purposes lead, earnest money first, so visual weight tracks
 * consequence. This is the prototype's `closingIntents` order, and it overrides
 * whatever order the BFF happens to send.
 */
const PURPOSE_ORDER = ['EMD', 'CashToClose'];

export interface InitiablePurpose {
    value: string;
    label: string;
    helper: string;
    icon: string;
}

// Module scoped on purpose. See the impression watcher below.
const dealsAlreadyReportedAsViewed = new Set<string>();

export function useParticipantInitiatedPayment() {
    const hostRouter = getHostRouter();
    const dashboardStore = useDashboardStore();

    const isSheetOpen = ref(false);
    const isStarting = ref(false);
    const startError = ref<string | null>(null);
    const purposes = computed<InitiablePurpose[]>(() => {
        const offered = dashboardStore.activeDeal?.participantInitiablePurposes ?? [];
        return PURPOSE_ORDER
            .filter((value) => offered.includes(value))
            .map((value) => ({ value, ...PURPOSE_PRESENTATION[value] }));
    });

    /**
     * Two conditions, both required. The role gate is ours, and a non-empty purpose
     * list is the BFF saying this deal is payable.
     *
     * The kill switch is deliberately not here. dashboard-bff gates
     * participantInitiablePurposes on buyer-initiated-payments-enable before it
     * resolves anything, so the flag being off arrives as an empty list. Reading it
     * again in the browser would be a second source of truth for one decision, and
     * it could not protect the bootstrap endpoint the way the server gate does.
     */
    const canInitiatePayment = computed(() =>
        isPaymentInitiatorRole(dashboardStore.activeDeal?.participantRole)
        && purposes.value.length > 0);

    // The card renders on canInitiatePayment, so its first true edge is the
    // impression. Reported once per deal per session: viewedForDeal is module
    // scoped, so leaving the dashboard and coming back remounts TaskView and does
    // not report a second impression for the same deal.
    watch(canInitiatePayment, (canInitiate) => {
        const dealId = dashboardStore.activeDeal?.dealId ?? null;
        if (!canInitiate || !dealId || dealsAlreadyReportedAsViewed.has(dealId)) return;
        dealsAlreadyReportedAsViewed.add(dealId);
        ParticipantPaymentTracking.trackEntryViewed(
            purposes.value.map((purpose) => purpose.value));
    }, { immediate: true });

    const openSheet = () => {
        startError.value = null;
        isSheetOpen.value = true;
        ParticipantPaymentTracking.trackPurposeSheetOpened();
    };

    const closeSheet = () => {
        isSheetOpen.value = false;
    };

    /**
     * The error renders on the card, which sits behind the sheet, so the sheet has
     * to close or the participant sees the rows re-enable and no message at all.
     */
    const failStart = (purpose: string) => {
        isSheetOpen.value = false;
        startError.value = 'Unable to start your payment. Please try again.';
        ParticipantPaymentTracking.trackStartFailed(purpose);
    };

    const startPayment = async (purpose: string) => {
        const dealId = dashboardStore.activeDeal?.dealId;
        if (!hostRouter || !dealId || isStarting.value) return;

        isStarting.value = true;
        startError.value = null;
        ParticipantPaymentTracking.trackPurposeSelected(purpose);

        try {
            const nav = await startParticipantInitiatedPaymentApi(dealId, purpose);
            if (!nav?.path) {
                failStart(purpose);
                return;
            }

            const slug = hostRouter.currentRoute.value.params.companySlug as string;
            isSheetOpen.value = false;
            hostRouter.push({
                path: withCompanySlug(nav.path, slug),
                query: { source: 'dashboard' },
            });
        } catch (error) {
            console.error('[Dashboard MFE] Failed to start participant-initiated payment:', error);
            failStart(purpose);
        } finally {
            isStarting.value = false;
        }
    };

    return {
        purposes,
        canInitiatePayment,
        isSheetOpen,
        isStarting,
        startError,
        openSheet,
        closeSheet,
        startPayment,
    };
}
